// OPM编辑器相关的API接口
import { useApiQuery, useApiPost } from '@packages/api-client/src/hooks';
import { apiClient } from '@packages/api-client';
import type {
  OntologyGraphTree,
  OPMGraphData,
  OntologyGraphPayload,
} from '../types/opm';

// OPM数据接口类型定义
export interface OPMDraftData {
  id: number;
  versionName: string;
  versionNumber: number;
  createdTime: string;
  updatedTime: string;
  createdBy: number;
  graph: OntologyGraphPayload | OPMGraphData;
  hash: string;
  timestamp: string;
}

export interface OPMDraftResponse {
  data: OPMDraftData;
}

export interface OPMSaveRequest {
  graph: OntologyGraphPayload;
  hash?: string; // hash是可选的，第一次保存时可能没有
}

// 服务器直接返回hash字符串作为data
export type OPMSaveResponse = string;

// 获取OPM草稿数据的hook
export function useOPMDraft(id: number, enabled: boolean = true) {
  return useApiQuery<OPMDraftData>(
    ['opm-draft', id],
    `/ontologies/${id}/draft`,
    {
      enabled: enabled && !!id,
      staleTime: 0, // 确保每次都从服务器获取最新数据
      retry: 1,
    }
  );
}

// 保存OPM草稿数据的hook
export function useSaveOPMDraft(id: number) {
  return useApiPost<OPMSaveResponse, OPMSaveRequest>(
    `/ontologies/${id}/draft`,
    {
      onSuccess: data => {
        console.log('OPM数据保存成功:', data);
      },
      onError: error => {
        console.error('OPM数据保存失败:', error);
      },
    }
  );
}

// 导入数据响应类型
export interface ImportDataResponse {
  nodes: any[];
  edges: any[];
}

// ======================== Process 相关类型定义 ========================

export type ProcessType = 'FUNCTION' | 'WORKFLOW' | 'JOB' | 'TOOL_CALL' | 'WEBHOOK';
export type ProcessDefinStatus = 'DRAFT' | 'VALID' | 'INVALID' | 'DEPLOYED';
export type FunctionLanguage = 'TYPESCRIPT' | 'PYTHON';
export type ProcessTriggerType = 'MANUAL' | 'PROCESS' | 'AGENT' | 'SYSTEM';
export type ProcessExecutionStatus = 'CREATED' | 'READY' | 'RUNNING' | 'RETRYING' | 'TERMINATED' | 'SUCCESS' | 'FAILED' | 'TIMEOUT' | 'CANCELLED';

export interface ProcessListItemDto {
  processNodeId: string;
  processName: string;
  codeName: string;
  description: string;
  tags: string[];
  processType: ProcessType;
  logicStatus: ProcessDefinStatus;
  supportsSyncExecution: boolean;
  stateful: boolean;
}

export interface ProcessFuncCodeDetailDto {
  id: number;
  ontologyId: number;
  processNodeId: string;
  processName: string;
  codeName?: string;
  description?: string;
  sourceCode: Partial<Record<FunctionLanguage, string>>;
  language: FunctionLanguage;
  type?: string;
  functionName: string;
  inputSchema: any;
  outputSchema: any;
  status: ProcessDefinStatus;
  tags?: string[];
  cpuTimeSeconds?: number;
  maxMemoryMb?: number;
  timeoutSeconds?: number;
  createdTime: string;
  updatedTime: string;
  createdBy: number;
  updatedBy: number;
}

export interface SaveProcessDefinitionRequest {
  processNodeId: string;
  sourceCode: Partial<Record<FunctionLanguage, string>>;
  language: FunctionLanguage;
  functionName?: string;
  versionTag?: string;
}

export interface SaveProcessInfoRequest {
  processName?: string;
  codeName?: string;
  description?: string;
  tags?: string[];
  processType?: string;
  language?: FunctionLanguage;
  inputSchema?: any;
  outputSchema?: any;
}

export interface SaveProcessCodeRequest {
  sourceCode: Partial<Record<FunctionLanguage, string>>;
  language: FunctionLanguage;
  functionName?: string;
  cpuTimeSeconds?: number;
  maxMemoryMb?: number;
  timeoutSeconds?: number;
  versionTag?: string;
}

export interface ValidateProcessLogicRequest {
  processNodeId: string;
  sourceCode: Partial<Record<FunctionLanguage, string>>;
  language: FunctionLanguage;
  functionName?: string;
}

export interface ValidateProcessLogicResponse {
  valid: boolean;
  message: string;
  inputSchema: string | null;
  outputSchema: string | null;
}

export interface ExecuteProcessRequest {
  processNodeId: string;
  inputPayload?: Record<string, any>;
  triggerType?: ProcessTriggerType;
  triggeredBy?: string;
  processInstanceId?: number | null;
  timeoutSeconds?: number;
  maxRetries?: number;
}

export interface ExecuteProcessResponse {
  executionId: number;
  status: ProcessExecutionStatus;
  outputPayload: Record<string, any>;
  userLogs: string | null;
  stdout: string | null;
  stderr: string | null;
  errorMessage: string | null;
  executionTimeMs: number;
  startedAt: string;
  finishedAt: string;
  retryCount: number;
}

export interface ProcessExecutionLogDto {
  id: number;
  ontologyId: number;
  processNodeId: string;
  processInstanceId: number | null;
  triggerType: ProcessTriggerType;
  triggeredBy: string;
  inputPayload: string;
  outputPayload: string;
  stdout: string | null;
  stderr: string | null;
  status: ProcessExecutionStatus;
  errorMessage: string | null;
  executionTimeMs: number;
  memoryUsedMb: number;
  cpuTimeMs: number;
  startedAt: string;
  finishedAt: string;
  retryCount: number;
  maxRetries: number;
}

export interface PageResponse<T> {
  content: T[];
  totalElements: number;
  totalPages: number;
  number: number;
  size: number;
}

// OPM API 类
export class OPMApi {
  /**
   * 导入数据（文件上传）
   * 设置5分钟超时，避免大文件上传超时
   * @param file CSV文件
   * @param nodeId 目标节点ID
   * @returns 导入的数据
   */
  static async importData(
    file: File,
    nodeId: string
  ): Promise<ImportDataResponse> {
    try {
      console.log('📤 开始导入数据:', {
        nodeId,
        fileName: file.name,
        fileSize: file.size,
      });

      const formData = new FormData();
      formData.append('csvFile', file);
      formData.append('nodeId', nodeId);

      const response = await apiClient
        .getInstance()
        .post('/editor/import-data', formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
          timeout: 300000, // 5分钟超时
        });

      console.log('📨 导入数据响应:', response.data);

      if (response.data.code !== 200) {
        throw new Error(response.data.message || '导入失败');
      }

      const data = response.data.data;
      // 确保 edges 总是数组
      return {
        nodes: data.nodes || [],
        edges: data.edges || [],
      };
    } catch (error: any) {
      console.error('❌ 导入数据失败:', error);
      throw error;
    }
  }

  /**
   * 获取 Process 列表
   */
  static async getProcessList(
    ontologyId: number,
    type?: ProcessType
  ): Promise<ProcessListItemDto[]> {
    try {
      const response = await apiClient
        .getInstance()
        .get(`/ontologies/${ontologyId}/processes`, {
          params: type ? { type } : undefined,
        });

      if (response.data.code !== 200) {
        throw new Error(response.data.message || '获取Process列表失败');
      }

      return response.data.data;
    } catch (error: any) {
      console.error('❌ 获取Process列表失败:', error);
      throw error;
    }
  }

  /**
   * 获取 Process 函数签名类型定义（.d.ts 字符串）
   */
  static async getProcessTypeDefs(
    ontologyId: number,
    processNodeId: string
  ): Promise<string> {
    try {
      const response = await apiClient
        .getInstance()
        .get(`/ontologies/${ontologyId}/processes/${processNodeId}/typedefs`);

      if (response.data.code !== 200) {
        throw new Error(response.data.message || '获取类型定义失败');
      }

      return response.data.data;
    } catch (error: any) {
      console.error('❌ 获取类型定义失败:', error);
      throw error;
    }
  }

  /**
   * 获取 Process 定义详情
   */
  static async getProcessDefinition(
    ontologyId: number,
    processNodeId: string
  ): Promise<ProcessFuncCodeDetailDto> {
    try {
      const response = await apiClient
        .getInstance()
        .get(`/ontologies/${ontologyId}/processes/${processNodeId}/definition`);

      if (response.data.code !== 200) {
        throw new Error(response.data.message || '获取Process定义失败');
      }

      return response.data.data;
    } catch (error: any) {
      console.error('❌ 获取Process定义失败:', error);
      throw error;
    }
  }

  /**
   * 获取 Process 自定义函数定义
   * 执行逻辑页的代码、语言及运行约束统一由该接口提供。
   */
  static async getProcessFunctionDefinition(
    ontologyId: number,
    processNodeId: string
  ): Promise<ProcessFuncCodeDetailDto> {
    try {
      const response = await apiClient
        .getInstance()
        .get(`/ontologies/${ontologyId}/processes/${processNodeId}/definitions/function`);

      if (response.data.code !== 200) {
        throw new Error(response.data.message || '获取自定义函数定义失败');
      }

      return response.data.data;
    } catch (error: any) {
      console.error('❌ 获取自定义函数定义失败:', error);
      throw error;
    }
  }


  /**
   * 保存 Process 自定义函数配置
   */
  static async saveProcessFunctionDefinition(
    ontologyId: number,
    processNodeId: string,
    request: SaveProcessCodeRequest
  ): Promise<ProcessFuncCodeDetailDto> {
    try {
      const response = await apiClient
        .getInstance()
        .put(`/ontologies/${ontologyId}/processes/${processNodeId}/definitions/function`, request);

      if (response.data.code !== 200) {
        throw new Error(response.data.message || '保存自定义函数配置失败');
      }

      return response.data.data;
    } catch (error: any) {
      console.error('❌ 保存自定义函数配置失败:', error);
      throw error;
    }
  }

  /**
   * 兼容旧调用方的方法名，底层已切换到新的 function 接口。
   */
  static async saveProcessCode(
    ontologyId: number,
    processNodeId: string,
    request: SaveProcessCodeRequest
  ): Promise<ProcessFuncCodeDetailDto> {
    return this.saveProcessFunctionDefinition(ontologyId, processNodeId, request);
  }

  /**
   * 保存 Process 基本信息（包括约束参数）
   */
  static async saveProcessInfo(
    ontologyId: number,
    processNodeId: string,
    request: SaveProcessInfoRequest
  ): Promise<void> {
    try {
      const response = await apiClient
        .getInstance()
        .put(`/ontologies/${ontologyId}/processes/${processNodeId}/definition`, request);

      if (response.data.code !== 200) {
        throw new Error(response.data.message || '保存基本信息失败');
      }
    } catch (error: any) {
      console.error('❌ 保存基本信息失败:', error);
      throw error;
    }
  }

  /**
   * 保存 Process 定义
   */
  static async saveProcessDefinition(
    ontologyId: number,
    processNodeId: string,
    request: SaveProcessDefinitionRequest
  ): Promise<ProcessFuncCodeDetailDto> {
    try {
      const response = await apiClient
        .getInstance()
        .put(`/ontologies/${ontologyId}/processes/${processNodeId}/definition`, request);

      if (response.data.code !== 200) {
        throw new Error(response.data.message || '保存Process定义失败');
      }

      return response.data.data;
    } catch (error: any) {
      console.error('❌ 保存Process定义失败:', error);
      throw error;
    }
  }

  /**
   * 验证 Process 函数代码
   */
  static async validateProcessLogic(
    ontologyId: number,
    processNodeId: string,
    request: ValidateProcessLogicRequest
  ): Promise<ValidateProcessLogicResponse> {
    try {
      const response = await apiClient
        .getInstance()
        .post(`/ontologies/${ontologyId}/processes/${processNodeId}/logic/validate`, request);

      if (response.data.code !== 200) {
        throw new Error(response.data.message || '验证函数代码失败');
      }

      return response.data.data;
    } catch (error: any) {
      console.error('❌ 验证函数代码失败:', error);
      throw error;
    }
  }

  /**
   * 删除 Process 定义
   */
  static async deleteProcessDefinition(
    ontologyId: number,
    processNodeId: string
  ): Promise<void> {
    try {
      const response = await apiClient
        .getInstance()
        .delete(`/ontologies/${ontologyId}/processes/${processNodeId}/definition`);

      if (response.data.code !== 200) {
        throw new Error(response.data.message || '删除Process定义失败');
      }
    } catch (error: any) {
      console.error('❌ 删除Process定义失败:', error);
      throw error;
    }
  }

  /**
   * 执行 Process
   */
  static async executeProcess(
    ontologyId: number,
    processNodeId: string,
    request: ExecuteProcessRequest
  ): Promise<ExecuteProcessResponse> {
    try {
      const response = await apiClient
        .getInstance()
        .post(`/ontologies/${ontologyId}/processes/${processNodeId}/execute`, request);

      if (response.data.code !== 200) {
        throw new Error(response.data.message || '执行Process失败');
      }

      return response.data.data;
    } catch (error: any) {
      console.error('❌ 执行Process失败:', error);
      throw error;
    }
  }

  /**
   * 获取执行日志列表（分页）
   */
  static async getProcessExecutionLogs(
    ontologyId: number,
    processNodeId: string,
    page: number = 0,
    size: number = 10
  ): Promise<PageResponse<ProcessExecutionLogDto>> {
    try {
      const response = await apiClient
        .getInstance()
        .get(`/ontologies/${ontologyId}/processes/${processNodeId}/executions`, {
          params: { page, size, sort: 'startedAt,desc' },
        });

      if (response.data.code !== 200) {
        throw new Error(response.data.message || '获取执行日志失败');
      }

      return response.data.data;
    } catch (error: any) {
      console.error('❌ 获取执行日志失败:', error);
      throw error;
    }
  }
}
